import { Component, OnInit } from '@angular/core';
import { BaseComponent } from '@core/components/base-component';
import { StatusEnum } from '@enums/Status';
import { Estimate } from '@interfaces/Estimate';
import { TranslateService } from '@ngx-translate/core';
import { LoadingService } from '@shared/services/loading.service';
import { ConfirmationService } from 'primeng/api';
import { Table } from 'primeng/table';
import { combineLatest } from 'rxjs';
import { EstimateService } from 'src/app/shared/services/estimate.service';
import { ClassificationRow } from './models/classification.row';

@Component({
  selector: 'pop-class-modal',
  templateUrl: 'class-modal.component.html',
  styleUrls: ['class-modal.component.less']
})
export class ClassModalComponent extends BaseComponent implements OnInit {
  public currentEstimate: Estimate;
  public isGroupApproved = true;
  public isStatusTypeNew: boolean;
  public rows: ClassificationRow[];

  public constructor(
    private confirmationService: ConfirmationService,
    private estimateService: EstimateService,
    private loadingService: LoadingService,
    private translate: TranslateService
  ) {
    super();
  }

  public ngOnInit() {
    this.loadingService.setIsLoading(true);

    this.subscribe(
      combineLatest([
        this.estimateService.observeCurrentEstimate(),
        this.estimateService.observeIsCurrentEstimateGroupApproved(),
        this.estimateService.getClassifications(true)
      ]).subscribe(([estimate, isGroupApproved, classifications]) => {
        this.currentEstimate = estimate;
        this.isStatusTypeNew = estimate.status === StatusEnum.New;
        this.isGroupApproved = isGroupApproved;
        this.rows = classifications.map(classification => {
          const classificationEstimate = this.currentEstimate.classificationEstimates.find(
            c => c.classificationId === classification.id
          );
          const row = new ClassificationRow(this.translate.currentLang, classification, classificationEstimate);
          return row;
        });

        this.loadingService.setIsLoading(false);
      })
    );
  }

  public async toggleClassification(event: MouseEvent, row: ClassificationRow) {
    event.preventDefault();

    if (row.classificationEstimate) {
      if (await this.confirmDelete()) {
        await this.loadingService.run(() => this.removeClassificationEstimate(row));
      }
      return;
    }

    await this.loadingService.run(() => this.addClassificationEstimate(row));
  }

  private async removeClassificationEstimate(row: ClassificationRow) {
    const classificationEstimate = row.classificationEstimate;
    row.classificationEstimate = undefined;

    await this.estimateService.deleteClassificationEstimate(classificationEstimate);
  }

  private async addClassificationEstimate(row: ClassificationRow) {
    row.classificationEstimate = this.estimateService.createClassificationEstimate(
      this.currentEstimate.id,
      row.classification
    );

    await this.estimateService.saveChangesAsync();
  }

  private confirmDelete() {
    const promise = new Promise<boolean>(resolve => {
      this.confirmationService.confirm({
        message: this.translate.instant('ERROR.NODE_DELETE') as string,
        header: this.translate.instant('HEADER.REMOVE_NODE') as string,
        icon: 'fas fa-exclamation-triangle',
        acceptLabel: this.translate.instant('BUTTON.CONFIRM') as string,
        rejectLabel: this.translate.instant('BUTTON.CANCEL') as string,
        key: 'global-confirmation-dialog',
        accept: () => resolve(true),
        reject: () => resolve(false)
      });
    });

    return promise;
  }

  public filterTable(table: Table, eventTarget: EventTarget) {
    const input = eventTarget as HTMLInputElement;
    table.filterGlobal(input.value, 'contains');
  }
}
